<template>
  <div class="h-full f-col space-y-2" :class="{ 'bg-white': whiteBackground }">
    <div class="relative f-col space-y-2">
      <base-picture
        :alt="image.alt"
        v-bind="size"
        class="w-full"
        :src="getResponsiveImageTransformations(image.src, {
          ...size,
          ...transformations,
        })"
      />
      <div v-if="rating?.showScore" class="z-1 flex items-center gap-1">
        <vf-rating size="xs" :value="rating?.score" />
      </div>
      <div v-style:c="textColor" class="space-y-2">
        <base-link :id class="line-clamp-2 self-start text-sm fw-medium" :to="url">
          {{ title }}
          <span class="absolute-0" />
        </base-link>
        <product-pricing
          class="grow text-sm"
          compact-omnibus
          :currency
          :price
          :show-discount-percentage="$feature.showDiscountPercentageOnCatalog"
          show-dynamic-pricing-on-plp
        />
      </div>
    </div>
    <cms-shared-button
      v-if="quickshop"
      :aria-describedby="id"
      class="relative mt-4"
      :class="brandClasses.addButton"
      data-test-id="product-card-add"
      :icon="{ id: 'plus', icon: 'plus' }"
      icon-size="sm"
      :size="{ sm: { size: addButtonSize } }"
      :style="ctaStyle || { sm: 'primary' }"
      :text="$t.add"
      :text-link-color
      :text-link-hover-color
      :text-link-style
      :text-link-underline
      @click="DialogQuickshop.open({ product, closeOnAdd: closeQuickshopOnAdd, gtmConfig })"
    />
    <dialog-quickshop class-content="<lg:pb-0" position="bottom" :size="$viewport.lg ? 'lg' : undefined" />
  </div>
</template>

<script lang="ts" setup>
import type { RecommendationStyle } from '#content/contexts/recommendation'
import type { ProductVariants } from '#root/api/clients/product/data-contracts'
import type { ProductContext } from '#types/gtm'
import type { ImageItem } from '#types/media'
import type { MonetateRating } from '#types/p13n'
import type { PriceFull } from '#types/product'
import { RecommendationStyleContextKey } from '#content/contexts/recommendation'

const { title, url, image, rating, id, currency, price, quickshop = true } = defineProps<{
  /**
   * Product name or title
   */
  title: string
  /**
   * Path to product detail page
   */
  url: string
  /**
   * Default product image
   */
  image: ImageItem
  /**
   * Product rating data
   */
  rating?: MonetateRating
  /**
   * Product id
   */
  id: string
  /**
   * Product price currency
   */
  currency: string
  /**
   * Product price data
   */
  price: PriceFull
  /**
   * Quickshop Toggle
   */
  quickshop?: boolean
  gtmConfig: ProductContext
}>()

const {
  addButtonSize,
  brandClasses,
  presets: { size, transformations }
} = useAppConfig().components.cms.productRecommendations
const { dialogOptions } = useAppConfig().components.product.card
const { $viewport } = useNuxtApp()
const route = useRoute()

const product = computed(() => ({
  id,
  name: title,
  url,
  price,
  currency,
  attributes: [],
  variants: {} as ProductVariants
}))

const {
  textColor,
  whiteBackground,
  ctaStyle,
  textLinkStyle,
  textLinkColor,
  textLinkHoverColor,
  textLinkUnderline,
} = inject(RecommendationStyleContextKey, {} as RecommendationStyle)

const closeQuickshopOnAdd = route.name?.toString().includes('cart')

const DialogQuickshop = createDialog('quickshop', dialogOptions)
</script>
